import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { api } from "./api";
import { useAuth } from "./auth";
import { trackWishlist } from "./analytics";

const KEY = "nexkey_wishlist";

export function readLocalWishlist(): string[] {
  if (typeof window === "undefined") return [];
  try {
    const raw = window.localStorage.getItem(KEY);
    return raw ? (JSON.parse(raw) as string[]) : [];
  } catch {
    return [];
  }
}

function writeLocal(ids: string[]) {
  if (typeof window === "undefined") return;
  window.localStorage.setItem(KEY, JSON.stringify(ids));
  window.dispatchEvent(new CustomEvent("nexkey:wishlist"));
}

interface WishlistCtx {
  ids: string[];
  ready: boolean;
  has: (id: string) => boolean;
  toggle: (id: string) => void;
  reload: () => Promise<void>;
}

const Ctx = createContext<WishlistCtx | null>(null);

export function WishlistProvider({ children }: { children: ReactNode }) {
  const { isLoggedIn, loading: authLoading } = useAuth();
  const [ids, setIds] = useState<string[]>([]);
  const [ready, setReady] = useState(false);
  const migratedFor = useRef<boolean>(false);

  // Logged-out: mirror localStorage (and cross-tab changes).
  useEffect(() => {
    if (authLoading || isLoggedIn) return;
    setIds(readLocalWishlist());
    setReady(true);
    const onChange = () => setIds(readLocalWishlist());
    window.addEventListener("nexkey:wishlist", onChange);
    window.addEventListener("storage", onChange);
    return () => {
      window.removeEventListener("nexkey:wishlist", onChange);
      window.removeEventListener("storage", onChange);
    };
  }, [authLoading, isLoggedIn]);

  const loadRemote = useCallback(async () => {
    try {
      const r = await api.favorites();
      setIds(r.data.map((g) => g.id));
    } catch {
      /* keep whatever we have */
    } finally {
      setReady(true);
    }
  }, []);

  // Logged-in: migrate any local favourites once, then load from the API.
  useEffect(() => {
    if (authLoading || !isLoggedIn) {
      migratedFor.current = false;
      return;
    }
    let cancelled = false;
    (async () => {
      if (!migratedFor.current) {
        migratedFor.current = true;
        const local = readLocalWishlist();
        if (local.length) {
          // Idempotent on the server; one failure must not break login.
          await Promise.allSettled(local.map((id) => api.addFavorite(id)));
          writeLocal([]);
        }
      }
      if (!cancelled) await loadRemote();
    })();
    return () => {
      cancelled = true;
    };
  }, [authLoading, isLoggedIn, loadRemote]);

  const toggle = useCallback(
    (id: string) => {
      const exists = ids.includes(id);
      const next = exists ? ids.filter((x) => x !== id) : [...ids, id];
      setIds(next);
      trackWishlist(exists ? "remove" : "add", id);

      if (!isLoggedIn) {
        writeLocal(next);
        return;
      }
      const req = exists ? api.removeFavorite(id) : api.addFavorite(id);
      void req.catch(() => setIds(ids)); // revert optimistic update
    },
    [ids, isLoggedIn],
  );

  const value = useMemo<WishlistCtx>(
    () => ({
      ids,
      ready,
      has: (id: string) => ids.includes(id),
      toggle,
      reload: loadRemote,
    }),
    [ids, ready, toggle, loadRemote],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useWishlist(): WishlistCtx {
  const v = useContext(Ctx);
  if (!v) throw new Error("useWishlist must be used inside <WishlistProvider>");
  return v;
}
